import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Product } from '../../models/product.model';
import { Company } from '../../models/company.model';
import { CompanyProducts } from '../../models/company.products.model';
import { CompanyProductVariants } from '../../models/company.product.variants.model';
import { ProductVariantIdParameter } from '../../models/product.variant.id.parameter.model';
import { authToken } from '../../scripts/authentication.script';

export class ProductDA {
  static apiResponse: ApiResponse

  public static async getProducts(
    id_company: string,
    product_name: string,
    category_id: string,
    sort_field: string,
    sort_mode: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyProducts: CompanyProducts | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/products.data.json').then(response => { companyProducts = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyProducts!.id_company === id_company) {
        companyProducts!.filter = { name: product_name , category: category_id, sort_mode: sort_mode, sort_field: sort_field }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyProducts
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products?name=${product_name}&category=${category_id}&sort_field=${sort_field}&sort_mode=${sort_mode}&page=${page}&limit=${limit}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async searchProductVariants(
    id_company: string,
    search: string,
    limit: number = 20,
    signal?: AbortSignal
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products/variants?search=${encodeURIComponent(search)}&limit=${limit}`

    let config: any = {
      headers: { Authorization: authToken() },
      signal
    }

    try {
      const response = await axios.get(url, config)
      return {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    } catch (error: any) {
      if (axios.isCancel(error)) {
        return { status: 0, message: 'cancelled', data: null }
      }
      return {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Unknown error',
        data: error.response?.data?.error ?? error.message
      }
    }
  }

  public static async getProductVariants(
    id_company: string,
    storage_center_id: string,
    compound_name: string,
    sort_field: string,
    sort_mode: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyProductVariants: CompanyProductVariants | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/product.variants.data.json').then(response => { companyProductVariants = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyProductVariants!.id_company === id_company) {
        companyProductVariants!.filter = { storage_center: storage_center_id, name: compound_name, sort_field: sort_field, sort_mode: sort_mode }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyProductVariants
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products/variants?storage_center=${storage_center_id}&name=${compound_name}&sort_field=${sort_field}&sort_mode=${sort_mode}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async getProduct(
    id_company: string,
    id_product: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let product: Product | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/product.data.json').then(response => { product = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (product!.id === id_product) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: product
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products/${id_product}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async postProduct(
    id_company: string,
    display_name: string,
    category_id: string,
    brand: string,
    names: { en: string, fr: string, it: string, de: string },
    accounts: { expense: string, vat: string, income: string },
    tags: string[],
    variants: { description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: { type: string, value: string, quantity: string }, features: { id_feature: string, name: string, value: string }[], photos: any[]
    }[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append('display_name', display_name)
      data.append('names[en]', names.en)
      data.append('names[fr]', names.fr)
      data.append('names[de]', names.de)
      data.append('names[it]', names.it)
      data.append('category', category_id)
      if (brand) data.append('brand', brand)
      data.append('accounts[expense]', accounts.expense)
      data.append('accounts[vat]', accounts.vat)
      data.append('accounts[income]', accounts.income)

      tags.forEach(tag => {
        data.append('tags', JSON.stringify(tag))
      })

      const variantsPayload = variants.map(({ photos, ...rest }) => rest)
      data.append('variants', JSON.stringify(variantsPayload))

      variants.forEach((variant) => {
        variant.photos.forEach((image, subIndex) => {
          if (image instanceof File) {
            data.append(`variants_photos`, image, `${variant.description_short}`)
          } else {
            data.append(`variants_photos_url[${variant.description_short}][${subIndex}]`, image)
          }
        })
      })

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async putProduct(
    id_company: string,
    id_product: string,
    display_name: string,
    category_id: string,
    brand: string,
    names: { en: string, fr: string, it: string, de: string },
    accounts: { expense: string, vat: string, income: string },
    tags: string[],
    variants: ProductVariantIdParameter[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let product: Product | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/product.data.json').then(response => { product = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (product!.id === id_product) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products/${id_product}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append('display_name', display_name)
      data.append('names[en]', names.en)
      data.append('names[fr]', names.fr)
      data.append('names[de]', names.de)
      data.append('names[it]', names.it)
      data.append('category', category_id)
      if (brand) data.append('brand', brand)
      data.append('accounts[expense]', accounts.expense)
      data.append('accounts[vat]', accounts.vat)
      data.append('accounts[income]', accounts.income)

      tags.forEach(tag => {
        data.append('tags', JSON.stringify(tag))
      })

      const variantsPayload = variants.map(({ photos, ...rest }) => rest)
      data.append('variants', JSON.stringify(variantsPayload))

      variants.forEach((variant) => {
        variant.photos.forEach((image, subIndex) => {
          if (image instanceof File) {
            data.append(`variants_photos`, image, `${variant.description_short}`)
          } else {
            data.append(`variants_photos_url[${variant.description_short}][${subIndex}]`, image)
          }
        })
      })

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }
    return this.apiResponse
  }

  public static async postVariant(
    id_company: string,
    id_product: string,
    variant: any
  ): Promise<ApiResponse> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products/${id_product}/variants`
    const config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }

    const data = new FormData()
    data.append('code', variant.code ?? '')
    data.append('description_short', variant.description_short ?? '')
    data.append('description_long', variant.description_long ?? '')
    data.append('sku', variant.sku ?? '')
    data.append('codebar', variant.codebar ?? '')
    data.append('price_sale', variant.price_sale ?? '0')
    data.append('price_purchase', variant.price_purchase ?? '0')
    data.append('tax_code_sale', variant.tax_code_sale ?? '')
    data.append('tax_code_purchase', variant.tax_code_purchase ?? '')
    data.append('tax_rate_sale', variant.tax_rate_sale ?? '0')
    data.append('tax_rate_purchase', variant.tax_rate_purchase ?? '0')
    data.append('unit', typeof variant.unit === 'object' ? JSON.stringify(variant.unit) : (variant.unit ?? ''))
    data.append('discount[type]', variant.discount?.type ?? '')
    data.append('discount[value]', variant.discount?.value ?? '0')
    data.append('discount[quantity]', variant.discount?.quantity ?? '0')

    if (variant.features) {
      variant.features.forEach((feature: any) => {
        data.append('feature', JSON.stringify(feature))
      })
    }

    if (variant.photos) {
      variant.photos.forEach((image: any) => {
        if (image instanceof File) {
          data.append('photos', image)
        }
      })
    }

    await axios.post(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Unknown error',
        data: error.response?.data?.error ?? error.message
      }
    })

    return this.apiResponse
  }

  public static async putVariant(
    id_company: string,
    id_product: string,
    id_variant: string,
    variant: any
  ): Promise<ApiResponse> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products/${id_product}/variants/${id_variant}`
    const config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }

    const data = new FormData()
    data.append('code', variant.code ?? '')
    data.append('description_short', variant.description_short ?? '')
    data.append('description_long', variant.description_long ?? '')
    data.append('sku', variant.sku ?? '')
    data.append('codebar', variant.codebar ?? '')
    data.append('price_sale', variant.price_sale ?? '0')
    data.append('price_purchase', variant.price_purchase ?? '0')
    data.append('tax_code_sale', variant.tax_code_sale ?? '')
    data.append('tax_code_purchase', variant.tax_code_purchase ?? '')
    data.append('tax_rate_sale', variant.tax_rate_sale ?? '0')
    data.append('tax_rate_purchase', variant.tax_rate_purchase ?? '0')
    data.append('unit', typeof variant.unit === 'object' ? JSON.stringify(variant.unit) : (variant.unit ?? ''))
    data.append('discount[type]', variant.discount?.type ?? '')
    data.append('discount[value]', variant.discount?.value ?? '0')
    data.append('discount[quantity]', variant.discount?.quantity ?? '0')

    if (variant.features) {
      variant.features.forEach((feature: any) => {
        data.append('feature', JSON.stringify(feature))
      })
    }

    if (variant.photos) {
      variant.photos.forEach((image: any) => {
        if (image instanceof File) {
          data.append('photos', image)
        }
      })
    }

    await axios.put(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Unknown error',
        data: error.response?.data?.error ?? error.message
      }
    })

    return this.apiResponse
  }

  public static async deleteVariant(
    id_company: string,
    id_product: string,
    id_variant: string
  ): Promise<ApiResponse> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products/${id_product}/variants/${id_variant}`
    const config: any = { headers: { Authorization: authToken() } }

    await axios.delete(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Unknown error',
        data: error.response?.data?.error ?? error.message
      }
    })

    return this.apiResponse
  }

  public static async deleteProducts(
    id_company: string,
    id_products: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let products: Product[] | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/products.data.json').then(response => { products = response.data.products }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (products!.some(item => id_products.includes(item.id))) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/products`

      let config: any = {
        headers: { Authorization: authToken() },
        data: { products: id_products }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }
}
